import * as Sentry from '@sentry/react';

type Severity = 'INFO' | 'WARNING' | 'ERROR';
type LogExtra = Record<string, unknown>;
type LogAttribute = string | number | boolean;

const sentryRelease = import.meta.env.VITE_APP_BUILD_SHA || undefined;

Sentry.init({
  dsn: 'https://9abe55543255dfe358a49c02c7319552@o4507376227713024.ingest.us.sentry.io/4507647570935808',
  enabled: process.env.NODE_ENV === 'production',
  enableLogs: true,
  release: sentryRelease,
  environment:
    process.env.NODE_ENV === 'development' ? 'development' : 'production',
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^\/api(\/|$)/],
  beforeSend: (event) => scrubEventRequestUrl(event),
  beforeSendTransaction: (event) => scrubEventRequestUrl(event),
});

const blockedKeyPattern =
  /token|authorization|password|secret|cookie|email|displayName/i;

const safeNestedKeyPattern =
  /id|amount|cents|rule|selection|accountType|paymentMethod|environment|savePaymentMethod|sendTransactionCompleteEmails/i;

const sensitiveQueryParamPattern =
  /apiKey|code|continueUrl|email|oobCode|tenantId/i;

const scrubSensitiveUrl = (url: string) => {
  try {
    const scrubbedUrl = new URL(url, window.location.origin);
    for (const key of scrubbedUrl.searchParams.keys()) {
      if (sensitiveQueryParamPattern.test(key)) {
        scrubbedUrl.searchParams.set(key, '[Filtered]');
      }
    }
    return scrubbedUrl.toString();
  } catch {
    return url;
  }
};

const scrubEventRequestUrl = <T extends { request?: { url?: string } }>(
  event: T,
) => {
  const requestUrl = event.request?.url;
  if (!requestUrl) return event;

  return {
    ...event,
    request: {
      ...event.request,
      url: scrubSensitiveUrl(requestUrl),
    },
  };
};

const isLogAttribute = (value: unknown): value is LogAttribute => {
  const valueType = typeof value;
  return (
    valueType === 'string' || valueType === 'number' || valueType === 'boolean'
  );
};

const toLogAttributeEntries = (
  value: unknown,
  path: string[],
): [string, LogAttribute][] => {
  const [key] = path.slice(-1);

  if (
    path.some(
      (pathKey) =>
        pathKey !== 'sendTransactionCompleteEmails' &&
        blockedKeyPattern.test(pathKey),
    )
  ) {
    return [];
  }

  if (isLogAttribute(value)) {
    return path.length === 1 || safeNestedKeyPattern.test(key)
      ? [[path.join('.'), value]]
      : [];
  }

  if (value === null || typeof value !== 'object' || path.length >= 2) {
    return [];
  }

  return Object.entries(value).flatMap(([nestedKey, nestedValue]) =>
    toLogAttributeEntries(nestedValue, [...path, nestedKey]),
  );
};

const toSentryLogAttributes = (extra?: LogExtra) =>
  Object.fromEntries(
    Object.entries(extra ?? {}).flatMap(([key, value]) =>
      toLogAttributeEntries(value, [key]),
    ),
  );

const sentryLogBySeverity = {
  INFO: 'info',
  WARNING: 'warn',
  ERROR: 'error',
} as const;

const sendLog = (severity: Severity, message: string, extra?: LogExtra) => {
  Sentry.logger[sentryLogBySeverity[severity]](
    message,
    toSentryLogAttributes(extra),
  );
};

export const telemetry = {
  info: (props: { message: string; extra?: LogExtra }) => {
    sendLog('INFO', props.message, props.extra);
  },
  warn: (props: { message: string; extra?: LogExtra }) => {
    sendLog('WARNING', props.message, props.extra);
  },
  error: (props: { error: unknown; message?: string; extra?: LogExtra }) => {
    const message = props.message ?? 'clients_app_error';
    sendLog('ERROR', message, {
      ...props.extra,
      errorMessage: String(props.error),
    });
    Sentry.captureException(props.error, {
      extra: toSentryLogAttributes(props.extra),
    });
  },
};
